/**
 * src/api/request.js
 *
 * Purpose: the app-wide `request(endpoint, params, resultKey, opts)` API-service
 * helper for Navixy API v2 — the backbone every namespaced Vuex module and every
 * screen goes through. Re-authored equivalent of the deployed pro-ui module
 * "2594" (export "w" = request, "x" = download-url, "S" = multipart upload).
 *
 * VERBATIM contract (re-greppable, survives minification):
 *   - POST body carries the session as field `hash` (value = `session_key` cookie)
 *   - URL = CONFIG.apiUrl + endpoint (apiRoot "//api.lacak.io")
 *   - response is unwrapped by `resultKey` (a field pluck) when given
 *   - error table on response.data.status.code:
 *       {2,3,4,11,103,"no_hash"} -> dispatch "user/logOut" {noServer:true}
 *       7 (and 247)              -> field-level errors (errors[].parameter)
 *       201                      -> email_not_found
 *       264                      -> rate-limit (templated wait)
 *       12                       -> server-error page
 *       HTTP >=500 or 404        -> "service_not_respond" (console only)
 *   - download: hash as query param; upload: hash as multipart form field.
 * The transport / handler bodies are re-authored.
 *
 * Reconstructed from lacak.io deployed behavior; app-specific contract verbatim,
 * internal impl re-authored.
 */

import axios from 'axios';
import CONFIG from './config.js';
import { getSessionKey } from './cookies.js';
import { getStore, getRouter } from './context.js';

// --- VERBATIM status-code buckets ---
// Session-invalid / auto-logout: expired or missing hash, blocked/deleted user,
// unactivated account.
const AUTO_LOGOUT_CODES = [2, 3, 4, 11, 103, 'no_hash'];
// Field-level validation errors.
const FIELD_ERROR_CODES = [7, 247];

const JSON_HEADERS = { 'Content-Type': 'application/json' };

/**
 * Normalize the 4th+ arguments. The task-level signature is
 * `request(endpoint, params, resultKey, opts)` where `opts` is
 * `{ skipErrors, skipHash, notyCtx }`; for compatibility with the app's
 * historical positional call-sites (`request(ep, p, key, skipErrors, skipHash, notyCtx)`)
 * a boolean 4th arg is also accepted.
 */
function normalizeOpts(arg4, arg5, arg6) {
  if (arg4 && typeof arg4 === 'object') {
    return {
      skipErrors: !!arg4.skipErrors,
      skipHash: !!arg4.skipHash,
      notyCtx: arg4.notyCtx || {}
    };
  }
  return {
    skipErrors: !!arg4,
    skipHash: !!arg5,
    notyCtx: arg6 || {}
  };
}

function parseFieldErrors(status) {
  const out = {};
  const errs = (status && status.errors) || [];
  for (let i = 0; i < errs.length; i++) {
    const e = errs[i];
    if (!e || e.parameter == null) continue;
    if (!out[e.parameter]) out[e.parameter] = [];
    out[e.parameter].push(e.error != null ? e.error : e);
  }
  return out;
}

/**
 * The status-code table (VERBATIM code lists). Mutates `err` with parsed
 * metadata and sets `err.handled` so the deferred reporter knows whether an
 * error was already surfaced.
 */
export function handleHttpError(err, notyCtx) {
  const resp = err && err.response;
  const httpStatus = resp && resp.status;

  // Collapse transport failures first.
  if (httpStatus >= 500 || httpStatus === 404) {
    err.status = { code: 'service_not_respond' };
    err.handled = true;
    // eslint-disable-next-line no-console
    console.log('server is down!');
    return err;
  }

  const status = (resp && resp.data && resp.data.status) || null;
  const code = status ? status.code : undefined;
  err.status = status;
  err.errorCode = code;
  err.handled = true;

  // {2,3,4,11,103,"no_hash"} -> force client-side logout.
  if (AUTO_LOGOUT_CODES.indexOf(code) !== -1) {
    const store = getStore();
    if (store) store.dispatch('user/logOut', { noServer: true });
    return err;
  }

  // 7 / 247 -> field-level error messages (errors[].parameter).
  if (FIELD_ERROR_CODES.indexOf(code) !== -1) {
    err.fieldErrors = parseFieldErrors(status);
    return err;
  }

  // 201 -> email / login not found.
  if (code === 201) {
    err.emailNotFound = true;
    return err;
  }

  // 264 -> resend rate-limit (server supplies a wait `remainder`).
  if (code === 264) {
    err.rateLimited = true;
    err.rateLimitRemainder = status ? status.remainder : undefined;
    return err;
  }

  // 12 -> server-error page.
  if (code === 12) {
    const store = getStore();
    if (store) {
      try {
        store.commit('service_error');
      } catch (e) {
        /* mutation may not be registered in every build */
      }
    }
    const router = getRouter();
    if (router && typeof router.push === 'function') {
      const p = router.push('/server-error');
      if (p && typeof p.catch === 'function') p.catch(function () {});
    }
    return err;
  }

  // The long "message" bucket: leave it to the caller / deferred reporter to
  // surface a localised `common.errors.<code>` string.
  err.handled = false;
  return err;
}

function reportError(err) {
  // Last-resort console reporting for anything the error table did not handle.
  // eslint-disable-next-line no-console
  console.error('[request] unhandled API error', err && (err.errorCode || err.message), err);
}

/**
 * request(endpoint, params, resultKey, opts)
 *
 * @param {string} endpoint   Navixy API v2 action string, e.g. "user/auth".
 * @param {object} [params]   POST body; `hash` is injected unless opts.skipHash.
 * @param {string} [resultKey] field to pluck from the response envelope.
 * @param {object|boolean} [opts] `{skipErrors, skipHash, notyCtx}` (object) or,
 *        for legacy call-sites, positional `skipErrors` boolean.
 * @returns {Promise<*>} the plucked field, or the whole `data` when no resultKey.
 */
export function request(endpoint, params, resultKey, opts, arg5, arg6) {
  const o = normalizeOpts(opts, arg5, arg6);
  const body = params || {};

  // Attach the session — the `session_key` cookie value travels as `hash`.
  if (!o.skipHash) body.hash = getSessionKey();

  const url = CONFIG.apiUrl + endpoint;

  return axios
    .post(url, body, { headers: JSON_HEADERS })
    .then(function (r) {
      return resultKey ? r.data[resultKey] : r.data;
    })
    .catch(function (err) {
      err.handled = false;
      if (!o.skipErrors) handleHttpError(err, o.notyCtx);
      setTimeout(function () {
        if (!err.handled) reportError(err);
      }, 1);
      throw err;
    });
}

/**
 * Build a query string, dropping null/undefined values.
 */
function toQueryString(params) {
  const parts = [];
  const keys = Object.keys(params || {});
  for (let i = 0; i < keys.length; i++) {
    const k = keys[i];
    const v = params[k];
    if (v == null) continue;
    parts.push(encodeURIComponent(k) + '=' + encodeURIComponent(v));
  }
  return parts.join('&');
}

/**
 * Download-URL builder (module "2594" export "x"). The `session_key` cookie
 * value is attached as the `hash` **query param**.
 */
export function downloadUrl(endpoint, params) {
  const p = Object.assign({}, params || {});
  p.hash = getSessionKey();
  return CONFIG.apiUrl + endpoint + '/?' + toQueryString(p);
}

/**
 * Open a download in a background target (matches `window.open(url, "download")`).
 * Returns the URL for testability.
 */
export function download(endpoint, params) {
  const url = downloadUrl(endpoint, params);
  if (typeof window !== 'undefined' && typeof window.open === 'function') {
    window.open(url, 'download');
  }
  return url;
}

function buildFormData(fields) {
  const fd = new FormData();
  const keys = Object.keys(fields || {});
  for (let i = 0; i < keys.length; i++) {
    const k = keys[i];
    const v = fields[k];
    if (v == null) continue;
    fd.append(k, v);
  }
  return fd;
}

/**
 * Multipart upload helper (module "2594" export "S"). The `session_key` cookie
 * value is attached as the `hash` **form field**.
 *
 * @param {string} endpoint
 * @param {object|FormData} params  plain fields (a `File`/`Blob` value is fine)
 *        or a pre-built FormData.
 * @param {string} [resultKey]
 * @param {object} [opts] `{skipErrors, notyCtx}`
 */
export function upload(endpoint, params, resultKey, opts) {
  const o = opts || {};
  const fd = params instanceof FormData ? params : buildFormData(params);
  fd.append('hash', getSessionKey());

  const url = CONFIG.apiUrl + endpoint;

  return axios
    .post(url, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
    .then(function (r) {
      return resultKey ? r.data[resultKey] : r.data;
    })
    .catch(function (err) {
      err.handled = false;
      if (!o.skipErrors) handleHttpError(err, o.notyCtx || {});
      setTimeout(function () {
        if (!err.handled) reportError(err);
      }, 1);
      throw err;
    });
}

export default request;
